import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import ErrorFilled from '@mui/icons-material/Error'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Alert, Box, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { ChangeEvent, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface UploadFileInputProps {
  setValue: UseFormSetValue<any>
  name: string
  label: string
  acceptedFormats: string
  acceptedFormatsLabel: string
  labelColor?: string
  labelFontWeight?: string
  icon?: React.ReactNode
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

export default function UploadFileInput({
  setValue,
  label,
  name,
  acceptedFormats,
  acceptedFormatsLabel,
  labelColor = 'info',
  labelFontWeight = 'normal',
  icon = <UploadFileIcon />
}: UploadFileInputProps) {
  const { t } = useTranslation()
  const [fileName, setFileName] = useState('')
  const [showError, setShowError] = useState(false)

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setShowError(false)
    const file = event.target.files?.[0]
    if (!file) return

    const acceptedTypes = acceptedFormats
      .split(',')
      .map(format => format.trim())
    const isValidFormat = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      } else {
        return file.type === type
      }
    })

    if (!isValidFormat) {
      setFileName('')
      setShowError(true)
      return
    }

    if (file.size > 3 * 1024 * 1024) {
      setFileName('')
      setShowError(true)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      setFileName(file.name)
      setValue(name, base64, { shouldDirty: true })
    }
    reader.readAsDataURL(file)
  }

  const handleDelete = () => {
    setFileName('')
    setValue(name, '', { shouldDirty: true })
    setShowError(false)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '.5rem' }}>
        <Button
          component='label'
          color='primary'
          role={undefined}
          variant='outlined'
          tabIndex={-1}
          startIcon={icon}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            textTransform: 'none',
            color: labelColor,
            fontWeight: labelFontWeight,
            padding: '0.375rem 1rem',
            background: 'rgba(178, 223, 219, 0.10)',
            width: '11.1875rem',
            height: '2.375rem'
          }}
        >
          {label}
          <VisuallyHiddenInput
            type='file'
            onChange={handleFileChange}
            accept={acceptedFormats}
          />
        </Button>
        {fileName && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '.625rem',
                border: '1px solid rgba(159, 168, 218, 0.70)',
                borderRadius: '.3125rem',
                background: 'rgba(159, 168, 218, 0.26)',
                padding: '.375rem .625rem'
              }}
            >
              <CheckCircleIcon color='success' />
              <Typography>{fileName}</Typography>
            </Box>
            <Button
              variant='outlined'
              size='small'
              color='error'
              onClick={handleDelete}
              sx={{
                textTransform: 'none',
                padding: '.25rem .625rem',
                borderRadius: '0.5rem',
                color: '#EF9A9A',
                border: '1px solid rgba(244, 67, 54, 0.50)'
              }}
              startIcon={
                <DeleteOutlineOutlinedIcon sx={{ color: '#EF9A9A' }} />
              }
            >
              {t('common.delete')}
            </Button>
          </>
        )}
      </Box>
      {showError && (
        <Alert
          variant='filled'
          severity='error'
          icon={<ErrorFilled sx={{ color: 'error.dark' }} />}
          sx={{
            backgroundColor: '#EF9A9A',
            color: 'primary.contrastText',

            '& .MuiAlert-message': {
              color: 'primary.contrastText'
            }
          }}
        >
          {t('common.invalid_logo.format_error_title')}
        </Alert>
      )}
      <Typography color='text.secondary' variant='caption'>
        {acceptedFormatsLabel}
      </Typography>
    </Box>
  )
}
